import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  ACTIVE_RUN_STATUSES,
  ProcessAutomationOutput,
  ProcessAutomationRun,
  ProcessAutomationSession,
  ProcessAutomationStatus,
} from '../model/process-automation';
import { rejectDigitalContact } from '../../../ccf-inbox/ccf-inbox.slice';

export const CCF_PROCESS_AUTOMATION_KEY = 'processAutomation';

export interface ProcessAutomationState {
  sessions: Record<string, ProcessAutomationSession>;
}

export const initialProcessAutomationState: ProcessAutomationState = {
  sessions: {},
};

/**
 * Creates an empty Process Automation session
 * @example emptySession()
 * @returns ProcessAutomationSession
 */
const emptySession = (): ProcessAutomationSession => ({
  isPickerOpen: false,
  pickerQuery: '',
  isViewingForm: false,
  isMinimized: false,
  triggeredWorkflowIds: [],
  dismissedResultRunIds: [],
});

/**
 * Returns the session of a contact, creating it when missing
 * @param state - ProcessAutomationState
 * @param contactId - string
 * @example getOrCreateSession(state, '123')
 * @returns ProcessAutomationSession
 */
const getOrCreateSession = (
  state: ProcessAutomationState,
  contactId: string
): ProcessAutomationSession => {
  if (!state.sessions[contactId]) {
    state.sessions[contactId] = emptySession();
  }
  return state.sessions[contactId];
};

/**
 * Returns the run of a contact when it matches the run id and is still active
 * @param state - ProcessAutomationState
 * @param contactId - string
 * @param runId - string
 * @example getActiveRunById(state, contactId, runId)
 * @returns ProcessAutomationRun | undefined
 */
const getActiveRunById = (
  state: ProcessAutomationState,
  contactId: string,
  runId: string
) => {
  const run = state.sessions[contactId]?.activeRun;
  return run && run.runId === runId && isRunActive(run) ? run : undefined;
};

/**
 * Checks whether a task is in progress, blocked or paused
 * @param run - ProcessAutomationRun
 * @example isRunActive(run)
 * @returns boolean
 */
export const isRunActive = (run?: ProcessAutomationRun) =>
  !!run && ACTIVE_RUN_STATUSES.includes(run.status);

/**
 * Applies a status, keeping a paused task paused until the agent resumes it
 * @param run - ProcessAutomationRun
 * @param status - ProcessAutomationStatus
 * @example setRunStatus(run, ProcessAutomationStatus.BLOCKED)
 */
const setRunStatus = (
  run: ProcessAutomationRun,
  status: ProcessAutomationStatus
) => {
  if (run.status === ProcessAutomationStatus.PAUSED) {
    run.statusBeforePause = status;
  } else {
    run.status = status;
  }
};

export const processAutomationSlice = createSlice({
  name: CCF_PROCESS_AUTOMATION_KEY,
  initialState: initialProcessAutomationState,
  reducers: {
    /**
     * Opens the Task Assistant picker, optionally with a search query
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(openTaskPicker(payload))
     */
    openTaskPicker(
      state,
      action: PayloadAction<{ contactId: string; query?: string }>
    ) {
      const session = getOrCreateSession(state, action.payload.contactId);
      session.isPickerOpen = true;
      session.pickerQuery = action.payload.query ?? '';
    },
    /**
     * Closes the Task Assistant picker
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(closeTaskPicker(payload))
     */
    closeTaskPicker(state, action: PayloadAction<{ contactId: string }>) {
      const session = getOrCreateSession(state, action.payload.contactId);
      session.isPickerOpen = false;
      session.pickerQuery = '';
    },
    /**
     * Updates the Task Assistant search query
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(setTaskPickerQuery(payload))
     */
    setTaskPickerQuery(
      state,
      action: PayloadAction<{ contactId: string; query: string }>
    ) {
      getOrCreateSession(state, action.payload.contactId).pickerQuery =
        action.payload.query;
    },
    /**
     * Selects a task from the Task Assistant (shows View Form / Run)
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(selectTask(payload))
     */
    selectTask(
      state,
      action: PayloadAction<{ contactId: string; workflowId?: string }>
    ) {
      const session = getOrCreateSession(state, action.payload.contactId);
      session.selectedWorkflowId = action.payload.workflowId;
      session.isViewingForm = false;
      if (action.payload.workflowId) {
        session.isPickerOpen = false;
        session.pickerQuery = '';
      }
    },
    /**
     * Opens or closes the form of the selected task (View Form / Back)
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(setViewingForm(payload))
     */
    setViewingForm(
      state,
      action: PayloadAction<{ contactId: string; isViewingForm: boolean }>
    ) {
      getOrCreateSession(state, action.payload.contactId).isViewingForm =
        action.payload.isViewingForm;
    },
    /**
     * Updates the progress of an active task (conversation turn, filled
     * values, attention / review flags)
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(patchWorkflowRun(payload))
     */
    patchWorkflowRun(
      state,
      action: PayloadAction<{
        contactId: string;
        runId: string;
        changes?: Partial<
          Pick<
            ProcessAutomationRun,
            | 'conversationIndex'
            | 'turnStage'
            | 'requiresAttention'
            | 'isReviewing'
          >
        >;
        values?: Record<string, any>;
        status?: ProcessAutomationStatus;
      }>
    ) {
      const { contactId, runId, changes, values, status } = action.payload;
      const run = getActiveRunById(state, contactId, runId);
      if (!run) return;
      Object.assign(run, changes ?? {});
      if (values) run.collectedValues = { ...run.collectedValues, ...values };
      if (status) setRunStatus(run, status);
    },
    /**
     * Collapses or expands the active task card
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(setTaskPanelMinimized(payload))
     */
    setTaskPanelMinimized(
      state,
      action: PayloadAction<{ contactId: string; isMinimized: boolean }>
    ) {
      getOrCreateSession(state, action.payload.contactId).isMinimized =
        action.payload.isMinimized;
    },
    /**
     * Marks a rule trigger as fired so it does not fire again for the contact
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(markWorkflowTriggered(payload))
     */
    markWorkflowTriggered(
      state,
      action: PayloadAction<{ contactId: string; workflowId: string }>
    ) {
      const session = getOrCreateSession(state, action.payload.contactId);
      if (!session.triggeredWorkflowIds.includes(action.payload.workflowId)) {
        session.triggeredWorkflowIds.push(action.payload.workflowId);
      }
    },
    /**
     * Hides a finished task result from the Copilot panel
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(dismissTaskResult(payload))
     */
    dismissTaskResult(
      state,
      action: PayloadAction<{ contactId: string; runId: string }>
    ) {
      const session = getOrCreateSession(state, action.payload.contactId);
      if (!session.dismissedResultRunIds.includes(action.payload.runId)) {
        session.dismissedResultRunIds.push(action.payload.runId);
      }
    },
    /**
     * Starts a task. Ignored while another interactive task is active.
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(startWorkflowRun(payload))
     */
    startWorkflowRun(
      state,
      action: PayloadAction<{ contactId: string; run: ProcessAutomationRun }>
    ) {
      const session = getOrCreateSession(state, action.payload.contactId);
      if (isRunActive(session.activeRun)) {
        return;
      }
      session.activeRun = action.payload.run;
      session.isPickerOpen = false;
      session.pickerQuery = '';
      session.selectedWorkflowId = undefined;
      session.isViewingForm = false;
      session.isMinimized = false;
    },
    /**
     * Records the confirmed form and moves the task back to in progress
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(submitWorkflowCard(payload))
     */
    submitWorkflowCard(
      state,
      action: PayloadAction<{
        contactId: string;
        runId: string;
        values: Record<string, any>;
      }>
    ) {
      const { contactId, runId, values } = action.payload;
      const run = getActiveRunById(state, contactId, runId);
      if (!run || run.status !== ProcessAutomationStatus.BLOCKED) return;
      run.collectedValues = { ...run.collectedValues, ...values };
      run.status = ProcessAutomationStatus.IN_PROGRESS;
    },
    /**
     * Pauses an active task
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(pauseWorkflowRun(payload))
     */
    pauseWorkflowRun(
      state,
      action: PayloadAction<{ contactId: string; runId: string }>
    ) {
      const run = getActiveRunById(
        state,
        action.payload.contactId,
        action.payload.runId
      );
      if (!run || run.status === ProcessAutomationStatus.PAUSED) return;
      run.statusBeforePause = run.status;
      run.status = ProcessAutomationStatus.PAUSED;
    },
    /**
     * Resumes a paused task where it stopped
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(resumeWorkflowRun(payload))
     */
    resumeWorkflowRun(
      state,
      action: PayloadAction<{ contactId: string; runId: string }>
    ) {
      const run = getActiveRunById(
        state,
        action.payload.contactId,
        action.payload.runId
      );
      if (!run || run.status !== ProcessAutomationStatus.PAUSED) return;
      run.status = run.statusBeforePause ?? ProcessAutomationStatus.BLOCKED;
      run.statusBeforePause = undefined;
    },
    /**
     * Ends the task with a terminal status
     * @param state - ProcessAutomationState
     * @param action - PayloadAction
     * @example dispatch(finishWorkflowRun(payload))
     */
    finishWorkflowRun(
      state,
      action: PayloadAction<{
        contactId: string;
        runId: string;
        status:
          | ProcessAutomationStatus.SUCCESS
          | ProcessAutomationStatus.CANCELLED;
        output?: ProcessAutomationOutput;
      }>
    ) {
      const { contactId, runId, status, output } = action.payload;
      const run = getActiveRunById(state, contactId, runId);
      if (!run) return;
      run.status = status;
      run.statusBeforePause = undefined;
      run.output = output;
    },
  },
  extraReducers: (builder) => {
    // A closed chat starts fresh when the same journey is simulated again
    // (Simulator-v2 reuses the journey id as the contact id)
    builder.addCase(rejectDigitalContact, (state, action) => {
      delete state.sessions[action.payload];
    });
  },
});

export const processAutomationReducer = processAutomationSlice.reducer;

export const {
  openTaskPicker,
  closeTaskPicker,
  setTaskPickerQuery,
  selectTask,
  setViewingForm,
  patchWorkflowRun,
  setTaskPanelMinimized,
  markWorkflowTriggered,
  dismissTaskResult,
  startWorkflowRun,
  submitWorkflowCard,
  pauseWorkflowRun,
  resumeWorkflowRun,
  finishWorkflowRun,
} = processAutomationSlice.actions;

/**
 * Reads the Process Automation slice from the root state
 * @param rootState - root redux state
 * @example getProcessAutomationState(state)
 * @returns ProcessAutomationState
 */
const getProcessAutomationState = (rootState: any): ProcessAutomationState =>
  rootState[CCF_PROCESS_AUTOMATION_KEY] ?? initialProcessAutomationState;

const EMPTY_SESSION = emptySession();

/**
 * Selects the Process Automation session of a contact
 * @param contactId - string
 * @example useSelector(selectProcessAutomationSession(contactId))
 * @returns ProcessAutomationSession
 */
export const selectProcessAutomationSession = (contactId: string) =>
  createSelector(
    getProcessAutomationState,
    (state) => state.sessions[contactId] ?? EMPTY_SESSION
  );
